import { ChangeEvent, ReactNode, useState } from "react";

import SectionCard from "@buildpy/ui/section-card";
import { Input } from "@buildpy/ui/input";
import { formatKoDate } from "@buildpy/core/lib/format";
import A4Fit from "@/modules/print/a4-fit";
import PrintSheet from "@/modules/print/sheet";
import { QrImage } from "@/modules/qr/qr-image";

export interface ConstructionNoticeDefaults {
  address: string;
  startDate: string;
  endDate: string;
  hours: string;
  work: string;
}

interface Props {
  defaults: ConstructionNoticeDefaults;
  contractorName: string;
  contractorTel: string;
  /** null 이면 QR 칸을 뺀다(무료 도구에서 주소를 비웠을 때). */
  qrSvg: string | null;
}

// 빈 날짜는 손으로 적을 밑줄 칸으로 인쇄된다.
const DateSlot = ({ value }: { value: string }) =>
  value ? (
    <span>{formatKoDate(value) ?? value}</span>
  ) : (
    <span aria-label="빈칸" role="img" className="inline-block w-40 border-b border-[#171717] align-bottom" data-testid="notice-date-blank">
      {"\u00a0"}
    </span>
  );

const Row = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex border-b border-[#d9d9d9] py-3">
    <div className="w-28 shrink-0 text-base font-semibold">{label}</div>
    <div className="min-w-0 flex-1 whitespace-pre-wrap break-words text-base">{value}</div>
  </div>
);

// 공사 안내문 A4 한 장 — 칸은 인쇄 직전에 손보는 용도라 저장하지 않는다(클라이언트 상태).
const ConstructionNotice = ({ defaults, contractorName, contractorTel, qrSvg }: Props) => {
  const [form, setForm] = useState(defaults);
  const set = (key: keyof ConstructionNoticeDefaults) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <div className="flex flex-col gap-4">
      <SectionCard className="print-hide mx-auto w-full max-w-[210mm] text-sm" data-testid="notice-editor" padding="sm">
        <p className="mb-3 text-xs text-muted-foreground">
          인쇄 전에 고칠 수 있습니다. 고친 내용은 저장되지 않습니다.
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <Input id="notice-address" label="단지·동호" name="notice-address" value={form.address} onChange={set("address")} />
          </div>
          <Input id="notice-start" label="공사 시작일" name="notice-start" type="date" value={form.startDate} onChange={set("startDate")} />
          <Input id="notice-end" label="공사 종료일" name="notice-end" type="date" value={form.endDate} onChange={set("endDate")} />
          <Input id="notice-hours" label="작업 시간" name="notice-hours" value={form.hours} onChange={set("hours")} />
          <Input id="notice-work" label="공사 내용" name="notice-work" value={form.work} onChange={set("work")} />
        </div>
      </SectionCard>

      {/* 폰은 종이만 A4 축소 — 위 편집 칸은 축소 밖이라 폰 크기 그대로. 최소 높이가 있어야 mt-auto 로 QR 이 하단에 붙고, 인쇄는 269mm 보다 작게 잡아 한 장을 넘지 않는다. */}
      <A4Fit>
        <PrintSheet className="flex flex-col gap-6 a4:min-h-[297mm] a4:p-12 print:min-h-[265mm]!">
          <h1 className="text-center text-3xl font-bold tracking-[0.2em]" data-testid="notice-title">
            인테리어 공사 안내
          </h1>
          <p className="text-base leading-relaxed">
            안녕하십니까. 아래 세대에서 인테리어 공사를 진행합니다. 공사 기간 동안 소음·먼지로 불편을 드려 대단히
            죄송합니다. 작업 시간을 지키고 공용 공간을 깨끗이 정리하겠습니다. 너그러운 양해 부탁드립니다.
          </p>
          <div className="border-t-2 border-[#171717]" data-testid="notice-fields">
            <Row label="단지·동호" value={form.address} />
            <Row
              label="공사 기간"
              value={
                <>
                  <DateSlot value={form.startDate} /> ~ <DateSlot value={form.endDate} />
                </>
              }
            />
            <Row label="작업 시간" value={form.hours} />
            <Row label="공사 내용" value={form.work} />
            <Row label="시공사" value={[contractorName, contractorTel].filter(Boolean).join(" · ")} />
          </div>
          {qrSvg !== null && (
            <div className="mt-auto flex flex-col items-center gap-2 pt-4" data-testid="notice-qr">
              <QrImage alt="간편 상담 접수 QR" className="size-40" svg={qrSvg} />
              <p className="text-center text-base font-semibold">우리 집도 인테리어 상담 — QR 을 찍어 주세요</p>
            </div>
          )}
        </PrintSheet>
      </A4Fit>
    </div>
  );
};

export default ConstructionNotice;
